import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'
import resumeMatcherApi from '../../../middleware/api/Recruitment/ResumeMatcher/resumeMatcherApi'
import getAllHrDataApi from '../../../middleware/api/EmployeeDirectory/EmployeesList/AddNewEmployee/hrDataApi'
import { ApiLoadingState } from '../../../middleware/api/apiList'
import { RootState } from '../../../stateStore'
import {
  ResumeMatchingData,
  GetAllResumesMatchingDataParams,
  ResumeMatcherState,
} from '../../../types/Recruitment/ResumeMatcher/resumeMatcherTypes'
import { GetHrData } from '../../../types/EmployeeDirectory/EmployeesList/AddNewEmployee/addNewEmployeeType'

export const getAllResumesMatchingData = createAsyncThunk(
  'resumeMatcher/getAllResumesMatchingData',
  async (params: GetAllResumesMatchingDataParams, thunkApi) => {
    try {
      console.log('Fetching all resumes matching data with params:', params)
      return await resumeMatcherApi.getAllResumesMatchingData(params)
    } catch (error) {
      const err = error as AxiosError
      console.error('Error fetching resumes matching data:', err.message)
      // eslint-disable-next-line sonarjs/no-duplicate-string
      return thunkApi.rejectWithValue(err.response?.status || 'Unknown error')
    }
  },
)

export const getAllHrData = createAsyncThunk<GetHrData[], void>(
  'resumeMatcher/getAllHrData',
  async () => {
    try {
      const result = await getAllHrDataApi.getAllHrData()
      return result || []
    } catch (error) {
      const err = error as AxiosError
      console.error('Error fetching HR data:', err.message)
      // Return empty array instead of rejecting to allow UI to continue
      return []
    }
  },
)

export const getExternalVendorsList = createAsyncThunk<string[], void>(
  'resumeMatcher/getExternalVendorsList',
  async (_, thunkApi) => {
    try {
      return await resumeMatcherApi.getExternalVendorsList()
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status || 'Unknown error')
    }
  },
)

export const getResumeFilePathById = createAsyncThunk(
  'resumeMatcher/getResumeFilePathById',
  async (id: number, thunkApi) => {
    try {
      const filePath = await resumeMatcherApi.getResumesPath(id)
      return { id, filePath }
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status || 'Unknown error')
    }
  },
)

const initialResumeMatcherState: ResumeMatcherState = {
  resumeMatchingDatas: [],
  totalCount: 0,
  isLoading: ApiLoadingState.idle,
  error: null,
  selectedJobTitle: '',
  selectedDate: 'Select Date',
  isSelected: 'all',
  matchingScore: 'all',
  fromDate: '',
  toDate: '',
  startIndex: 0,
  endIndex: 20,
  pageSize: 20,
  selectedHrName: '',
  availabilityStatus: '',
  sourceType: 'all',
  vendorName: '',
  vendorsList: [],
  resumeFilePathById: {},
  resumeFilePathLoading: ApiLoadingState.idle,
}

const resumeMatcherSlice = createSlice({
  name: 'resumeMatcher',
  initialState: initialResumeMatcherState,
  reducers: {
    setSelectedJobTitle: (state, action) => {
      state.selectedJobTitle = action.payload
    },
    setSelectedDate: (state, action) => {
      state.selectedDate = action.payload
    },
    setIsSelected: (state, action) => {
      state.isSelected = action.payload
    },
    setMatchingScore: (state, action) => {
      state.matchingScore = action.payload
    },
    setFromDate: (state, action) => {
      state.fromDate = action.payload
    },
    setToDate: (state, action) => {
      state.toDate = action.payload
    },
    setStartIndex: (state, action) => {
      state.startIndex = action.payload
    },
    setEndIndex: (state, action) => {
      state.endIndex = action.payload
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload
    },
    setSelectedHrName: (state, action) => {
      state.selectedHrName = action.payload
    },
    setAvailabilityStatus: (state, action) => {
      state.availabilityStatus = action.payload
    },
    setSourceType: (state, action) => {
      state.sourceType = action.payload
      if (action.payload === 'all') {
        state.vendorName = ''
      }
    },
    setVendorName: (state, action) => {
      state.vendorName = action.payload
    },
    clearFilters: (state) => {
      state.selectedJobTitle = ''
      state.selectedDate = 'Select Date'
      state.isSelected = 'all'
      state.matchingScore = 'all'
      state.fromDate = ''
      state.toDate = ''
      state.startIndex = 0
      state.endIndex = 20
      state.resumeMatchingDatas = []
      state.totalCount = 0
      state.selectedHrName = ''
      state.availabilityStatus = ''
      state.sourceType = 'all'
      state.vendorName = ''
    },
    clearError: (state) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllResumesMatchingData.pending, (state) => {
        state.isLoading = ApiLoadingState.loading
      })
      .addCase(getAllResumesMatchingData.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.resumeMatchingDatas = action.payload.list || []
        state.totalCount = action.payload.size || 0
      })
      .addCase(getAllResumesMatchingData.rejected, (state, action) => {
        state.isLoading = ApiLoadingState.failed
        state.error = action.error.message || 'Failed to fetch data'
      })
      .addCase(getExternalVendorsList.fulfilled, (state, action) => {
        state.vendorsList = action.payload
      })
      .addCase(getExternalVendorsList.rejected, (state) => {
        state.vendorsList = []
      })
      .addCase(getResumeFilePathById.pending, (state) => {
        state.resumeFilePathLoading = ApiLoadingState.loading
      })
      .addCase(getResumeFilePathById.fulfilled, (state, action) => {
        state.resumeFilePathLoading = ApiLoadingState.succeeded
        const { id, filePath } = action.payload as {
          id: number
          filePath: string
        }
        if (id) {
          state.resumeFilePathById[id] = filePath || ''
        }
      })
      .addCase(getResumeFilePathById.rejected, (state, action) => {
        state.resumeFilePathLoading = ApiLoadingState.failed
        state.error = action.error.message || 'Failed to fetch resume path'
      })
  },
})

// Selectors
const selectResumeMatchingData = (state: RootState): ResumeMatchingData[] =>
  state.resumeMatcher.resumeMatchingDatas

const selectTotalCount = (state: RootState): number =>
  state.resumeMatcher.totalCount

const selectIsLoading = (state: RootState): ApiLoadingState =>
  state.resumeMatcher.isLoading

const selectError = (state: RootState): string | null =>
  state.resumeMatcher.error

const selectPageSize = (state: RootState): number =>
  state.resumeMatcher.pageSize

const selectSelectedJobTitle = (state: RootState): string =>
  state.resumeMatcher.selectedJobTitle

const selectSelectedDate = (state: RootState): string =>
  state.resumeMatcher.selectedDate

const selectIsSelected = (state: RootState): string | boolean =>
  state.resumeMatcher.isSelected

const selectMatchingScore = (state: RootState): string =>
  state.resumeMatcher.matchingScore

const selectFromDate = (state: RootState): string =>
  state.resumeMatcher.fromDate

const selectToDate = (state: RootState): string => state.resumeMatcher.toDate

const selectStartIndex = (state: RootState): number =>
  state.resumeMatcher.startIndex

const selectEndIndex = (state: RootState): number =>
  state.resumeMatcher.endIndex

const selectSelectedHrName = (state: RootState): string =>
  state.resumeMatcher.selectedHrName

const selectAvailabilityStatus = (state: RootState): string =>
  state.resumeMatcher.availabilityStatus

const selectSourceType = (state: RootState): string =>
  state.resumeMatcher.sourceType

const selectVendorName = (state: RootState): string =>
  state.resumeMatcher.vendorName

const selectVendorsList = (state: RootState): string[] =>
  state.resumeMatcher.vendorsList

const selectResumeFilePathById =
  (id: number) =>
  (state: RootState): string =>
    state.resumeMatcher.resumeFilePathById[id] || ''

const selectResumeFilePathLoading = (state: RootState): ApiLoadingState =>
  state.resumeMatcher.resumeFilePathLoading

export const resumeMatcherSelectors = {
  selectResumeMatchingData,
  selectTotalCount,
  selectIsLoading,
  selectError,
  selectPageSize,
  selectSelectedJobTitle,
  selectSelectedDate,
  selectIsSelected,
  selectMatchingScore,
  selectFromDate,
  selectToDate,
  selectStartIndex,
  selectEndIndex,
  selectSelectedHrName,
  selectAvailabilityStatus,
  selectSourceType,
  selectVendorName,
  selectVendorsList,
  selectResumeFilePathById,
  selectResumeFilePathLoading,
}

const resumeMatcherThunk = {
  getAllResumesMatchingData,
  getAllHrData,
  getExternalVendorsList,
  getResumeFilePathById,
}

export const resumeMatcherService = {
  ...resumeMatcherThunk,
  actions: resumeMatcherSlice.actions,
  selectors: resumeMatcherSelectors,
}

export default resumeMatcherSlice.reducer
